import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import colors from 'common/constants/colors';
import {
  LINK_CHECK_STATUS,
  countLabel,
  filterProductsBySearchText,
  firstWordOf,
  groupSimilarProducts,
  productNameOf,
} from '../utils/ndcProductLink';
import LinkFactChip from './LinkFactChip';
import LinkedNdcRows from './LinkedNdcRows';
import ProductIdChip from './ProductIdChip';

const SEARCH_SHOWN_ABOVE_PRODUCT_COUNT = 4;
// About four product cards. A longer list scrolls on its own, so the catalog product on the left
// stays in view.
const PRODUCT_LIST_MAX_HEIGHT = 360;

// The name is the catalog display name, which already carries strength, pack size and form, so
// they are not repeated under it. Manufacturer belongs to an NDC, not to a product, so it is shown
// on the NDC rows only.
const SimilarProductRow = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      data-testid='similar-product'
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: colors.cardBorderGrey,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}
      >
        <Typography variant='body2' sx={{ fontWeight: 700 }}>
          {productNameOf(product)}
        </Typography>
        <ProductIdChip productId={product.productId} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
        {product.tags.map((tag) => (
          <LinkFactChip key={tag} fact={tag} />
        ))}
        <Box sx={{ ml: 'auto' }}>
          {product.ndcs.length > 0 ? (
            <Button
              size='small'
              onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={isExpanded}
              sx={{ px: 0.5, minWidth: 0, fontWeight: 700, textTransform: 'none' }}
            >
              {countLabel(product.ndcs.length, 'NDC', 'NDCs')}
            </Button>
          ) : (
            <Typography variant='caption' color='text.secondary'>
              No NDCs yet
            </Typography>
          )}
        </Box>
      </Box>
      <Collapse in={isExpanded} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <LinkedNdcRows ndcs={product.ndcs} />
        </Box>
      </Collapse>
    </Box>
  );
};

const linkCheckProductShape = PropTypes.shape({
  catalogId: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  drugName: PropTypes.string,
  strength: PropTypes.string,
  dosageForm: PropTypes.string,
  formAlias: PropTypes.string,
  packSizeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  packSizeUnit: PropTypes.string,
  ndcs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
});

SimilarProductRow.propTypes = {
  product: linkCheckProductShape.isRequired,
};

// POM-6600 — the right side of the review step: products a wrong pick is easily confused with.
// Nothing here is suggested or switched to.
const SimilarProductsPanel = ({ linkCheck, linkCheckStatus, onRetry, pickedDrugName }) => {
  const [searchText, setSearchText] = useState('');
  const isLoaded = linkCheckStatus === LINK_CHECK_STATUS.LOADED && Boolean(linkCheck);
  const similarProducts = isLoaded ? linkCheck.similarProducts : [];
  const matchingProducts = filterProductsBySearchText(similarProducts, searchText);
  const { differentName, sameDrugName } = groupSimilarProducts(matchingProducts, pickedDrugName);
  const firstWord = firstWordOf(pickedDrugName);
  const isSearching = searchText.trim() !== '';

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: colors.cardBorderGrey,
        bgcolor: colors.dialogSurfaceGrey,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant='caption'
          sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
        >
          SIMILAR PRODUCTS
        </Typography>
        {isLoaded && (
          <Chip
            size='small'
            label={
              isSearching
                ? `${matchingProducts.length} of ${similarProducts.length}`
                : similarProducts.length
            }
            sx={{ height: 20 }}
          />
        )}
      </Box>

      {linkCheckStatus === LINK_CHECK_STATUS.LOADING && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant='body2' color='text.secondary'>
            Checking similar products…
          </Typography>
        </Box>
      )}

      {linkCheckStatus === LINK_CHECK_STATUS.FAILED && (
        <Box>
          <Typography variant='body2' sx={{ color: colors.warningAlertText }}>
            Couldn&apos;t load the check. You can still confirm; compare the product with the bottle
            or vial label first.
          </Typography>
          <Button
            size='small'
            onClick={onRetry}
            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{ px: 0, minWidth: 0 }}
          >
            Retry
          </Button>
        </Box>
      )}

      {isLoaded && (
        <>
          <Typography variant='body2' color='text.secondary'>
            Active products whose name starts with “{firstWord}”. Compare them with the bottle or
            vial label.
          </Typography>

          {similarProducts.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              No other active products start with “{firstWord}”.
            </Typography>
          )}

          {similarProducts.length > SEARCH_SHOWN_ABOVE_PRODUCT_COUNT && (
            <TextField
              size='small'
              fullWidth
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder='Search by drug name or Product ID'
              id='SimilarProductsPanel-search-input'
              sx={{ bgcolor: 'background.paper' }}
              InputProps={{
                inputProps: { 'aria-label': 'Search similar products' },
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {isSearching && matchingProducts.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              No similar products match “{searchText.trim()}”.
            </Typography>
          )}

          {matchingProducts.length > 0 && (
            <Box
              data-testid='similar-products-list'
              sx={{
                maxHeight: PRODUCT_LIST_MAX_HEIGHT,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                pr: 0.5,
              }}
            >
              {[
                { groupLabel: 'Different name', products: differentName },
                { groupLabel: 'Same drug name', products: sameDrugName },
              ]
                .filter(({ products }) => products.length > 0)
                .map(({ groupLabel, products }) => (
                  <Box key={groupLabel} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='body2' sx={{ fontWeight: 700 }}>
                      {groupLabel}
                    </Typography>
                    {products.map((product) => (
                      <SimilarProductRow key={product.catalogId} product={product} />
                    ))}
                  </Box>
                ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

SimilarProductsPanel.propTypes = {
  linkCheck: PropTypes.shape({
    similarProducts: PropTypes.arrayOf(linkCheckProductShape),
  }),
  linkCheckStatus: PropTypes.oneOf(Object.values(LINK_CHECK_STATUS)).isRequired,
  onRetry: PropTypes.func.isRequired,
  pickedDrugName: PropTypes.string.isRequired,
};
SimilarProductsPanel.defaultProps = {
  linkCheck: null,
};

export default SimilarProductsPanel;
